import {
  CheckIcon,
  FileIcon,
  FolderIcon,
  HardDriveIcon,
  Loader2Icon,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { type JSX, useEffect, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import {
  PickerBreadcrumb,
  type BlockingRow,
  type BrowseCrumb,
  type RowStanding,
} from '@/features/microsoft/picker-chrome.js';
import {
  createOneDriveKnowledgeSource,
  createOneDriveScope,
  createOneDriveSetupSession,
  getOneDriveSetupDrive,
  listOneDriveSetupItems,
  OneDriveApiError,
  type OneDriveItem,
  type OneDriveScope,
  type OneDriveSetupHit,
} from '@/lib/api/onedrive.js';
import { knowledgeSourceKeys } from '@/lib/api/workspace-knowledge.js';
import { notify } from '@/lib/notify.js';

import { OneDriveFinder } from './OneDriveFinder.js';
import {
  oneDriveErrorPresentation,
  type OneDriveErrorPresentation,
} from './onedrive-error.js';

function setupErrorPresentation(
  reason: unknown,
  fallback: string,
): OneDriveErrorPresentation {
  const presentation = oneDriveErrorPresentation(reason);
  return reason instanceof Error &&
    presentation.message === 'OneDrive is unavailable.'
    ? { ...presentation, message: reason.message || fallback }
    : presentation;
}

function appendRows(
  current: OneDriveItem[],
  incoming: OneDriveItem[],
): OneDriveItem[] {
  const ids = new Set(current.map((item) => item.itemId));
  return [...current, ...incoming.filter((item) => !ids.has(item.itemId))];
}

/**
 * The rows with the one a search led to moved to the top, so it is the first thing on screen in a
 * folder that can hold hundreds — added to the page when the page did not carry it.
 */
function foundFirst(
  rows: readonly OneDriveItem[],
  foundId: string | null,
): OneDriveItem[] {
  const found = rows.find((row) => row.itemId === foundId);
  return found
    ? [found, ...rows.filter((row) => row.itemId !== foundId)]
    : [...rows];
}

/**
 * Folders first, then files, each in the order a person reads them — a folder is a way deeper, a
 * file is a leaf, the same order the SharePoint tab uses. Graph returns its own, which looks like no order at all
 * to somebody scanning a long drive for one name. Sorting is on what is loaded, since these
 * endpoints page and take no ordering — a later page's name lands in place as it arrives.
 */
function byName(rows: readonly OneDriveItem[]): OneDriveItem[] {
  return [...rows].sort((left, right) => {
    if (left.kind !== right.kind) return left.kind === 'folder' ? -1 : 1;
    return left.name.localeCompare(right.name, undefined, {
      sensitivity: 'base',
    });
  });
}

type PendingAction =
  | { kind: 'browse' }
  | { kind: 'open'; folderId: string }
  | { kind: 'load-more' }
  | { kind: 'root' }
  | { kind: 'add'; folderId: string };

/**
 * One connection's personal OneDrive, with an Add beside each folder.
 *
 * The counterpart of the SharePoint browser, and it exists for the same reason: choosing what a
 * connection exposes means comparing the chosen folders against the ones beside them, which a picker
 * hidden behind a button cannot show. The connection is fixed by whoever mounts this — the folder
 * list already belongs to one connection — so unlike the standalone setup dialog there is nothing to
 * choose first.
 *
 * The whole drive is the first row rather than a button of its own, which is what makes this read
 * like the SharePoint half: there, a whole site is a row among the sites. It still keeps its
 * confirmation, because it is the one choice here that keeps growing after it is made — it covers
 * every folder that exists now and every folder made later.
 *
 * Search sits above the tree, as it does on the SharePoint half: one dialog, one way to find
 * things. A hit is shown inside the folder that holds it rather than added from the results.
 */
export function OneDriveFolderBrowser({
  workspaceId,
  connectionId,
  onAdded,
  standingOf,
  onReplace,
}: {
  workspaceId: string;
  connectionId: string;
  /**
   * Called after each successful add, with the folder that was granted. The connector dialog only
   * needs the signal and ignores the scope; the Artifacts dialog seeds its cache with it and opens
   * the new folder, which is why it travels rather than being re-read.
   */
  onAdded: (scope: OneDriveScope) => void;
  /**
   * How a row stands against the folders already on the list, answered by whoever holds that list.
   * `ancestors` is the row's own path, outermost first, which is what places it — the same shape
   * the SharePoint half passes as its browse position.
   */
  standingOf: (itemId: string, ancestors: readonly string[]) => RowStanding;
  /**
   * Takes the named folders off the list, so a wider one can be added over them. Left out by a
   * surface that cannot remove a folder; its rows then stay refused rather than offering something
   * nobody can carry out.
   */
  onReplace?: (scopeIds: readonly string[]) => Promise<void>;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [driveRoot, setDriveRoot] = useState<OneDriveItem | null>(null);
  const [rows, setRows] = useState<OneDriveItem[]>([]);
  const [folderStack, setFolderStack] = useState<OneDriveItem[]>([]);
  /**
   * The row waiting on a confirmation, with whatever it would take off the list.
   *
   * One piece of state for both questions, because a row can raise both at once: the whole drive
   * keeps growing after it is chosen, AND it may cover folders somebody already added.
   */
  const [confirming, setConfirming] = useState<{
    folder: OneDriveItem;
    blocking: readonly BlockingRow[];
  } | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  const [error, setError] = useState<OneDriveErrorPresentation | null>(null);
  /** The row a search led to, kept at the top of its folder. */
  const [foundId, setFoundId] = useState<string | null>(null);
  const busy = pendingAction !== null;
  /**
   * The path of the level on screen, outermost first — the drive root and every folder opened to
   * get here. Every row at this level is placed against it, and it is empty until the drive root
   * has been read.
   */
  const ancestors = driveRoot
    ? [driveRoot.itemId, ...folderStack.map((folder) => folder.itemId)]
    : [];

  useEffect(() => {
    if (!setupToken && pendingAction === null && !error) void start();
  }, [setupToken]);

  async function start(): Promise<void> {
    setPendingAction({ kind: 'browse' });
    setError(null);
    try {
      const session = await createOneDriveSetupSession(
        workspaceId,
        connectionId,
      );
      const [root, page] = await Promise.all([
        getOneDriveSetupDrive(workspaceId, session.setupToken),
        listOneDriveSetupItems(workspaceId, session.setupToken),
      ]);
      setSetupToken(session.setupToken);
      setExpiresAt(session.expiresAt);
      setDriveRoot(root);
      setRows(page.items);
      setNextCursor(page.nextCursor);
      setFolderStack([]);
    } catch (reason) {
      setError(setupErrorPresentation(reason, 'Could not browse OneDrive.'));
    } finally {
      setPendingAction(null);
    }
  }

  async function openFolder(folder: OneDriveItem): Promise<void> {
    if (!setupToken) return;
    setPendingAction({ kind: 'open', folderId: folder.itemId });
    setError(null);
    try {
      const page = await listOneDriveSetupItems(workspaceId, setupToken, {
        parentItemId: folder.itemId,
      });
      setRows(page.items);
      setNextCursor(page.nextCursor);
      setFolderStack((current) => [...current, folder]);
    } catch (reason) {
      setError(setupErrorPresentation(reason, 'Could not open this folder.'));
    } finally {
      setPendingAction(null);
    }
  }

  /**
   * Opens the folder that holds a search hit, with every folder above it on the path — which is
   * what tells the rows whether a folder on the list already covers them — and the hit first.
   */
  async function reveal(hit: OneDriveSetupHit): Promise<boolean> {
    if (!setupToken) return false;
    const parentItemId = hit.ancestors.at(-1)?.itemId;
    setPendingAction({ kind: 'root' });
    setError(null);
    try {
      const page = await listOneDriveSetupItems(
        workspaceId,
        setupToken,
        parentItemId ? { parentItemId } : undefined,
      );
      setFolderStack(
        hit.ancestors.map((folder) => ({
          itemId: folder.itemId,
          parentItemId: null,
          name: folder.name,
          kind: 'folder' as const,
          contentType: null,
          size: 0,
          etag: null,
          webUrl: null,
          lastModifiedAt: null,
        })),
      );
      setRows(
        page.items.some((row) => row.itemId === hit.itemId)
          ? page.items
          : [hit, ...page.items],
      );
      setNextCursor(page.nextCursor);
      setFoundId(hit.itemId);
      return true;
    } catch (reason) {
      setError(setupErrorPresentation(reason, 'Could not open this folder.'));
      return false;
    } finally {
      setPendingAction(null);
    }
  }

  async function loadCurrentPage(cursor: string | null) {
    if (!setupToken) {
      throw new Error('The OneDrive setup session is unavailable.');
    }
    const parentItemId = folderStack.at(-1)?.itemId;
    if (parentItemId) {
      return listOneDriveSetupItems(workspaceId, setupToken, {
        parentItemId,
        ...(cursor ? { cursor } : {}),
      });
    }
    return cursor
      ? listOneDriveSetupItems(workspaceId, setupToken, { cursor })
      : listOneDriveSetupItems(workspaceId, setupToken);
  }

  async function loadMore(): Promise<void> {
    if (!setupToken || !nextCursor) return;
    setPendingAction({ kind: 'load-more' });
    setError(null);
    try {
      const page = await loadCurrentPage(nextCursor);
      setRows((current) => appendRows(current, page.items));
      setNextCursor(page.nextCursor);
    } catch (reason) {
      if (
        reason instanceof OneDriveApiError &&
        reason.code === 'ONEDRIVE_INVALID_CURSOR'
      ) {
        try {
          const page = await loadCurrentPage(null);
          setRows(page.items);
          setNextCursor(page.nextCursor);
        } catch (restartReason) {
          setError(
            setupErrorPresentation(
              restartReason,
              'Could not restart the OneDrive folder listing.',
            ),
          );
        }
      } else {
        setError(
          setupErrorPresentation(reason, 'Could not load more from OneDrive.'),
        );
      }
    } finally {
      setPendingAction(null);
    }
  }

  /**
   * Jumps back up the path. `depth` is how many opened folders to keep, so -1 is the drive root —
   * the same shape the SharePoint half's breadcrumb uses, so the two paths behave identically.
   */
  async function backToDepth(depth: number): Promise<void> {
    if (!setupToken) return;
    setPendingAction({ kind: 'root' });
    setError(null);
    const keep = folderStack.slice(0, depth + 1);
    const parentItemId = keep.at(-1)?.itemId;
    try {
      const page = await listOneDriveSetupItems(
        workspaceId,
        setupToken,
        parentItemId ? { parentItemId } : undefined,
      );
      setFolderStack(keep);
      setRows(page.items);
      setNextCursor(page.nextCursor);
    } catch (reason) {
      setError(setupErrorPresentation(reason, 'Could not browse OneDrive.'));
    } finally {
      setPendingAction(null);
    }
  }

  /** Grants one folder, at maximum access, and hands the refreshed list back to whoever mounted this. */
  /**
   * Puts the folder on the workspace's knowledge shelf, which is the rest of what "added at full
   * strength" means: without it a folder was reachable when somebody named it and invisible to
   * search, and every row read "Not searchable" — the SharePoint side had always indexed on add.
   *
   * A failure here does not undo the folder. The grant is the thing that was asked for, indexing is
   * the part that can be retried, and the row's Settings is where to turn it back on.
   */
  async function index(
    scope: OneDriveScope,
    folder: OneDriveItem,
  ): Promise<void> {
    try {
      await createOneDriveKnowledgeSource(scope.scopeId, {
        workspaceId,
        itemId: folder.itemId,
      });
      await queryClient.invalidateQueries({
        queryKey: knowledgeSourceKeys.list(workspaceId),
      });
    } catch (reason) {
      /** Already on the shelf — the state that was asked for, so it is not reported as a failure. */
      if (
        reason instanceof OneDriveApiError &&
        reason.code === 'ONEDRIVE_KNOWLEDGE_ALREADY_ADDED'
      ) {
        await queryClient.invalidateQueries({
          queryKey: knowledgeSourceKeys.list(workspaceId),
        });
        return;
      }
      /**
       * The folder IS on the list — only the indexing did not happen, so this is a warning and not
       * a failure, and it says which of the two it was.
       *
       * The server's own reason travels with it. Swallowing it left one sentence for every cause,
       * and the commonest cause by far is a whole drive being larger than one knowledge source may
       * be: "could not be indexed for search" told nobody that, or what to do instead.
       *
       * The name is the one on the row, not Graph's. A drive root comes back called `root`, and
       * that is the word the message carried.
       */
      const presentation = oneDriveErrorPresentation(reason);
      notify.warning(
        `“${folderLabel(folder)}” is on the list, but it was not added to the knowledge base. ${presentation.message} ${presentation.remediation}`,
      );
    }
  }

  /** What a folder is called on screen — never Graph's `root` for the drive itself. */
  function folderLabel(folder: OneDriveItem): string {
    return folder.itemId === driveRoot?.itemId
      ? 'The entire OneDrive'
      : folder.name;
  }

  /**
   * The Add on a row: straight through when nothing is in the way, a confirmation when something is.
   *
   * A row that covers folders already on the list used to be a dead button with an instruction under
   * it — go and remove four folders, one at a time, each behind its own confirmation. The work is the
   * same either way; the difference is who does it. So the picker offers to do it, once, with the
   * names on screen and the one thing that does not come back said out loud.
   *
   * Without `onReplace` there is nobody to do the removing, so the row stays as it was: refused, and
   * saying why.
   */
  function askToAdd(folder: OneDriveItem, blocking: readonly BlockingRow[]) {
    const isDrive = folder.itemId === driveRoot?.itemId;
    if (blocking.length === 0 && !isDrive) {
      void addFolder(folder);
      return;
    }
    setConfirming({ folder, blocking });
  }

  /**
   * Takes the blocking folders off the list, then adds the one that was asked for.
   *
   * In that order, and it cannot be the other way round: the server refuses a folder that overlaps
   * an existing one, so the old ones have to be gone before the new one can exist. A failure after
   * the removals leaves the list shorter and the new folder unadded — which is why the confirmation
   * says what comes off before any of it happens.
   */
  async function confirmAdd(): Promise<void> {
    const pending = confirming;
    if (!pending) return;
    setConfirming(null);
    if (pending.blocking.length > 0) {
      if (!onReplace) return;
      setPendingAction({ kind: 'add', folderId: pending.folder.itemId });
      setError(null);
      try {
        await onReplace(pending.blocking.map((row) => row.id));
      } catch (reason) {
        setError(
          setupErrorPresentation(reason, 'Could not take them off the list.'),
        );
        return;
      } finally {
        setPendingAction(null);
      }
    }
    await addFolder(pending.folder);
  }

  async function addFolder(folder: OneDriveItem): Promise<void> {
    if (!setupToken) return;
    if (expiresAt && Date.parse(expiresAt) <= Date.now()) {
      setError({
        message: 'This setup session expired. Start folder setup again.',
        remediation: 'Close and reopen this dialog to create a fresh session.',
        canRetry: true,
        operationCompleted: false,
      });
      return;
    }
    setPendingAction({ kind: 'add', folderId: folder.itemId });
    setError(null);
    try {
      const scope = await createOneDriveScope(setupToken, {
        workspaceId,
        rootItemId: folder.itemId,
        interactiveAccess: 'read_write',
        automationAccess: 'read_write',
      });
      await index(scope, folder);
      onAdded(scope);
    } catch (reason) {
      setError(setupErrorPresentation(reason, 'Could not add this.'));
    } finally {
      setPendingAction(null);
    }
  }

  /**
   * Why a row cannot be added, in words, under its name — or nothing when it can.
   *
   * It is on screen and not in a `title` because a title is not an answer: on a touch screen it
   * never appears at all, and on a pointer it appears only if somebody thinks to hover a control
   * that already looks dead. "The entire OneDrive" was reported as simply unavailable for exactly
   * that reason — the row greyed out and said nothing.
   */
  /** The rows already on the list that this one would swallow, empty when it swallows none. */
  function blockingOf(
    itemId: string,
    rowAncestors: readonly string[],
  ): readonly BlockingRow[] {
    const standing = standingOf(itemId, rowAncestors);
    return standing.kind === 'contains' ? standing.blocking : [];
  }

  function blockedNote(
    itemId: string,
    rowAncestors: readonly string[],
  ): string | null {
    const standing = standingOf(itemId, rowAncestors);
    if (standing.kind !== 'contains') return null;
    const count = standing.blocking.length;
    if (!onReplace) {
      return count === 1
        ? 'An item inside this one is on the list — remove it to add the whole thing.'
        : `${count} items inside this one are on the list — remove them to add the whole thing.`;
    }
    return count === 1
      ? 'Covers an item already on the list.'
      : `Covers ${count} items already on the list.`;
  }

  /**
   * The Add beside a row, or the mark that says there is nothing to press. The SharePoint half draws
   * exactly this, from the same four standings — a person reading one tab has already learnt the
   * other.
   */
  function rowAction(
    itemId: string,
    rowAncestors: readonly string[],
    name: string,
    onAdd: () => void,
  ): JSX.Element {
    const standing = standingOf(itemId, rowAncestors);
    if (standing.kind === 'added') {
      return (
        <span className="inline-flex h-8 shrink-0 items-center gap-1 px-2 text-xs text-muted-foreground">
          <CheckIcon className="size-3" aria-hidden />
          Added
        </span>
      );
    }
    if (standing.kind === 'covered') {
      return (
        <span
          className="inline-flex h-8 shrink-0 items-center gap-1 px-2 text-xs text-muted-foreground"
          title={`Already reachable through ${standing.by}.`}
        >
          <CheckIcon className="size-3" aria-hidden />
          Included
        </span>
      );
    }
    const adding =
      pendingAction?.kind === 'add' && pendingAction.folderId === itemId;
    /** Refused only where nobody can clear what is in the way — see {@link blockedNote}. */
    const blocked = standing.kind === 'contains' && !onReplace;
    return (
      <Button
        size="sm"
        variant="outline"
        aria-label={
          blocked
            ? `Cannot add ${name} — remove what is inside it on the list first`
            : `Add ${name}`
        }
        disabled={busy || blocked}
        onClick={onAdd}
      >
        {adding ? <Loader2Icon className="animate-spin" aria-hidden /> : 'Add'}
      </Button>
    );
  }

  /** The whole-drive row's reason, read once so it is not recomputed while rendering it. */
  const driveNote = driveRoot ? blockedNote(driveRoot.itemId, []) : null;

  const crumbs: BrowseCrumb[] = [
    { key: 'root', label: 'OneDrive', onSelect: () => void backToDepth(-1) },
  ];
  folderStack.forEach((folder, depth) => {
    crumbs.push({
      key: folder.itemId,
      label: folder.name,
      onSelect: () => void backToDepth(depth),
    });
  });

  /** What covers the level on screen, if anything, said once above the rows rather than per row. */
  const levelStanding = driveRoot
    ? standingOf(
        folderStack.at(-1)?.itemId ?? driveRoot.itemId,
        folderStack.length > 0 ? ancestors.slice(0, -1) : [],
      )
    : ({ kind: 'free' } as RowStanding);
  const coveringFolder =
    levelStanding.kind === 'covered'
      ? levelStanding.by
      : levelStanding.kind === 'added'
        ? (folderStack.at(-1)?.name ?? 'your OneDrive')
        : null;

  return (
    <div
      aria-label="Browse this OneDrive"
      aria-busy={busy}
      className="flex min-h-0 flex-1 flex-col gap-2"
    >
      {/* Starting and failing keep the tree's box, so the column keeps its frame while it waits. */}
      {!setupToken && (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-border p-2">
          {error ? (
            <>
              <p className="px-3 text-center text-sm text-destructive">
                {error.message}
              </p>
              <Button variant="outline" onClick={() => void start()}>
                Try again
              </Button>
            </>
          ) : (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="animate-spin" aria-hidden />
              Starting…
            </span>
          )}
        </div>
      )}

      {setupToken && (
        /*
          Where you are lives INSIDE the box, pinned above the rows that scroll under it — the same
          arrangement the SharePoint half uses, for the same reason: sitting outside, it pushed this
          box a breadcrumb's height below the one beside it and the two columns started on different
          lines.
        */
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border">
          <OneDriveFinder
            workspaceId={workspaceId}
            setupToken={setupToken}
            disabled={busy}
            onReveal={reveal}
          >
            <div className="flex shrink-0 flex-col gap-1 border-b border-border px-2 py-2">
              <PickerBreadcrumb crumbs={crumbs} label="OneDrive path" />
              {coveringFolder !== null && (
                <p className="text-xs text-muted-foreground">
                  All of {coveringFolder} is on the list, so nothing in here
                  needs adding.
                </p>
              )}
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
              {/*
              The whole drive is a row of the list, not a button above it. It is the OneDrive
              counterpart of a site row on the SharePoint side, so it reads the same way and — more
              to the point — it can say WHY it cannot be pressed. As a button it went grey whenever
              anything was already on the list, with the reason in a `title` nobody on a touch
              screen ever sees, and it read as simply broken.
            */}
              {driveRoot && folderStack.length === 0 && (
                <div className="flex items-start gap-2 rounded-md px-2 py-2 text-sm">
                  <HardDriveIcon
                    className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-medium">
                      The entire OneDrive
                    </span>
                    {driveNote && (
                      <span className="text-xs text-muted-foreground">
                        {driveNote}
                      </span>
                    )}
                  </span>
                  {rowAction(driveRoot.itemId, [], 'the entire OneDrive', () =>
                    askToAdd(driveRoot, blockingOf(driveRoot.itemId, [])),
                  )}
                </div>
              )}
              {rows.length === 0 && (
                <p className="px-2 py-2 text-sm text-muted-foreground">
                  This folder is empty.
                </p>
              )}
              {foundFirst(byName(rows), foundId).map((row) => {
                if (row.kind === 'file') {
                  return (
                    <div
                      key={row.itemId}
                      className="flex items-start gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted"
                    >
                      <span className="flex min-w-0 flex-1 items-start gap-2">
                        <FileIcon
                          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                        <span className="truncate">{row.name}</span>
                      </span>
                      {rowAction(row.itemId, ancestors, row.name, () =>
                        askToAdd(row, []),
                      )}
                    </div>
                  );
                }
                const opening =
                  pendingAction?.kind === 'open' &&
                  pendingAction.folderId === row.itemId;
                const note = blockedNote(row.itemId, ancestors);
                return (
                  <div
                    key={row.itemId}
                    className="flex items-start gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted"
                  >
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-start gap-2 text-left"
                      aria-label={
                        opening
                          ? `Opening folder ${row.name}…`
                          : `Open folder ${row.name}`
                      }
                      aria-busy={opening}
                      disabled={busy}
                      onClick={() => void openFolder(row)}
                    >
                      {opening ? (
                        <Loader2Icon
                          className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground"
                          aria-hidden
                        />
                      ) : (
                        <FolderIcon
                          className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                      )}
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">{row.name}</span>
                        {note && (
                          <span className="text-xs text-muted-foreground">
                            {note}
                          </span>
                        )}
                      </span>
                    </button>
                    {rowAction(row.itemId, ancestors, row.name, () =>
                      askToAdd(row, blockingOf(row.itemId, ancestors)),
                    )}
                  </div>
                );
              })}
            </div>
            {nextCursor && (
              <Button
                variant="outline"
                className="m-2 shrink-0 self-start"
                disabled={busy}
                onClick={() => void loadMore()}
              >
                {pendingAction?.kind === 'load-more'
                  ? 'Loading more…'
                  : 'Load more'}
              </Button>
            )}
          </OneDriveFinder>

          {error && (
            <div className="shrink-0 border-t border-border px-2 py-2 text-sm">
              <p className="text-destructive">{error.message}</p>
              <p className="text-muted-foreground">{error.remediation}</p>
            </div>
          )}
        </div>
      )}

      <AppDialog
        open={confirming !== null}
        onOpenChange={(next) => !next && setConfirming(null)}
        title={
          confirming?.folder.itemId === driveRoot?.itemId
            ? 'Use your entire OneDrive?'
            : `Add “${confirming?.folder.name ?? ''}”?`
        }
        description={
          confirming?.folder.itemId === driveRoot?.itemId
            ? 'This grants access to every current and future folder in your OneDrive, at read and write, in chat and in automations.'
            : 'This folder covers others that are already on the list, at read and write, in chat and in automations.'
        }
      >
        <div className="flex flex-col gap-4 pt-4">
          {confirming && confirming.blocking.length > 0 && (
            <div className="flex flex-col gap-2 rounded-lg border border-border p-3 text-sm">
              <p className="font-medium">
                {confirming.blocking.length === 1
                  ? 'This folder comes off the list'
                  : `These ${confirming.blocking.length} items come off the list`}
              </p>
              <ul className="flex flex-col gap-1 text-muted-foreground">
                {confirming.blocking.map((row) => (
                  <li key={row.id} className="truncate">
                    {row.name}
                  </li>
                ))}
              </ul>
              {/*
                Said plainly because it is the part that does not come back. What is being added
                reaches everything they reached, so nothing becomes unreachable — but each of them
                carries its own access settings and its own place in the knowledge base, and both
                go. Anything indexed under them is deleted and indexed again from scratch.
              */}
              <p className="text-xs text-muted-foreground">
                Everything they reached stays reachable. What they lose is their
                own access settings and their place in the knowledge base —
                anything indexed under them is deleted, and indexed again from
                scratch under the new one.
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button onClick={() => void confirmAdd()}>
              {confirming && confirming.blocking.length > 0
                ? 'Replace and add'
                : 'Continue with entire OneDrive'}
            </Button>
          </div>
        </div>
      </AppDialog>
    </div>
  );
}
